import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { format, subDays, startOfWeek, addDays, subWeeks } from 'date-fns';
import { Timestamp } from 'firebase/firestore';
import { useDailyMetrics } from '@/hooks/useDeepWorkMetrics';
import { useWeeklyPlan, getWeekId } from '@/hooks/useWeeklyPlan';
import { useRitualDates } from '@/hooks/useDays';
import { Timeline } from '@/components/Timeline';
import { Header } from '@/components/Header';
import { WeekStrip } from '@/components/WeekStrip';
import { FAB } from '@/components/FAB';
import { QuickAddPanel } from '@/components/panels/QuickAddPanel';
import { QuickAddTaskPanel } from '@/components/panels/QuickAddTaskPanel';
import { BlockDetailPanel } from '@/components/panels/BlockDetailPanel';
import { TaskPanelPanel } from '@/components/panels/TaskPanelPanel';
import { SettingsPanel } from '@/components/panels/SettingsPanel';
import { QuickCapturePanel } from '@/components/panels/QuickCapturePanel';
import { QuickCaptureButton } from '@/components/QuickCaptureButton';
import { FixedScheduleReminder } from '@/components/FixedScheduleReminder';
import { InboxPanel } from '@/components/panels/InboxPanel';
import { MITSelector } from '@/components/MITSelector';
import { DailyStoicQuote } from '@/components/DailyStoicQuote';
import { SubconsciousMindQuote } from '@/components/SubconsciousMindQuote';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { MissedShutdownBanner } from '@/components/MissedShutdownBanner';
import { RightSidebar } from '@/components/RightSidebar';
import { NewBlockData } from '@/components/panels/QuickAddContent';
import { NewTaskData } from '@/components/panels/QuickAddTaskContent';
import { CaptureData } from '@/components/panels/QuickCaptureContent';
import { useBlocks, formatDateKey } from '@/hooks/useBlocks';
import { useTasks } from '@/hooks/useTasks';
import { useCaptures } from '@/hooks/useCaptures';
import { useDailyMITs } from '@/hooks/useDailyMITs';
import { BlockWithRevisions, Task, Capture, DailyMIT } from '@/types';
import { TIMELINE_START_HOUR, TIMELINE_END_HOUR } from '@/constants';
import { DailyViewSkeleton } from './DailyViewSkeleton';
import {
  openPanelAtom,
  togglePanelAtom,
  closePanelAtom,
  selectedBlockAtom,
  quickAddStartTimeAtom,
  quickAddDateAtom,
  schedulingTaskAtom,
} from '@/stores/panels';
import { useSetAtom } from 'jotai';
import styles from './DailyView.module.css';

export function DailyView() {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isShutdownConfirmOpen, setIsShutdownConfirmOpen] = useState(false);
  const openPanel = useSetAtom(openPanelAtom);
  const togglePanel = useSetAtom(togglePanelAtom);
  const closePanel = useSetAtom(closePanelAtom);
  const setSelectedBlock = useSetAtom(selectedBlockAtom);
  const setQuickAddStartTime = useSetAtom(quickAddStartTimeAtom);
  const setQuickAddDate = useSetAtom(quickAddDateAtom);
  const setSchedulingTask = useSetAtom(schedulingTaskAtom);

  // Fetch blocks for the selected date
  const { blocks, isLoading, completedCount, totalCount, createBlock, updateBlock, deleteBlock } =
    useBlocks({
      date: formatDateKey(selectedDate),
      includeRevisions: true,
    });

  // Fetch tasks
  const { tasks, isLoading: isTasksLoading, reorderTasks, createTask, completeTask } = useTasks();

  // Fetch captures for quick capture and inbox
  const {
    captures,
    inboxCaptures,
    inboxCount,
    createCapture,
    processCapture,
    archiveCapture,
    deleteCapture,
  } = useCaptures();

  // Fetch daily MITs
  const dateKey = formatDateKey(selectedDate);
  const {
    mits,
    completedCount: mitsCompleted,
    addMIT,
    updateMIT,
    completeMIT,
    removeMIT,
  } = useDailyMITs(dateKey);

  // Fetch deep work metrics for the selected date
  const { metrics: dailyMetrics } = useDailyMetrics(dateKey);

  // Fetch weekly plan for day themes
  const weekId = getWeekId(selectedDate);
  const { weeklyPlan } = useWeeklyPlan({ weekId });

  // Fetch ritual completion status for nearby weeks only (not all 175 days)
  const visibleDateKeys = useMemo(() => {
    const today = new Date();
    const start = subWeeks(startOfWeek(today, { weekStartsOn: 1 }), 4);
    const totalDays = 9 * 7; // 4 weeks before + current + 4 weeks after = 63 days
    return Array.from({ length: totalDays }, (_, i) => format(addDays(start, i), 'yyyy-MM-dd'));
  }, []);
  const { ritualCompletedDates, isLoading: isRitualLoading } = useRitualDates(visibleDateKeys);

  // Missed shutdown banner state
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const yesterdayKey = useMemo(() => format(subDays(new Date(), 1), 'yyyy-MM-dd'), []);
  const showMissedBanner =
    !isBannerDismissed && !isRitualLoading && !ritualCompletedDates.has(yesterdayKey);

  const mitsTotal = mits.length;
  const isInitialLoading = isLoading || isTasksLoading;

  // Calculate dynamic timeline range based on blocks
  // Extends beyond default hours if blocks exist outside that range
  const { effectiveStartHour, effectiveEndHour } = useMemo(() => {
    let minHour = TIMELINE_START_HOUR;
    let maxHour = TIMELINE_END_HOUR;

    for (const block of blocks) {
      const blockStartHour = Math.floor(block.startTime / 60);
      const blockEndHour = Math.ceil((block.startTime + block.duration) / 60);

      if (blockStartHour < minHour) {
        minHour = blockStartHour;
      }
      if (blockEndHour > maxHour) {
        maxHour = blockEndHour;
      }
    }

    return {
      effectiveStartHour: minHour,
      effectiveEndHour: Math.min(maxHour, 24), // Cap at midnight
    };
  }, [blocks]);

  // Handle date selection from week strip
  const handleDateSelect = useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  // Handle block click - open detail sheet
  const handleBlockClick = useCallback(
    (block: BlockWithRevisions) => {
      closePanel('quickAdd');
      setSelectedBlock(block);
      openPanel('blockDetail');
    },
    [closePanel, openPanel, setSelectedBlock]
  );

  // Handle empty slot click - open quick add at that time
  const handleEmptySlotClick = useCallback(
    (startTime: number) => {
      closePanel('blockDetail');
      setQuickAddStartTime(startTime);
      setQuickAddDate(formatDateKey(selectedDate));
      openPanel('quickAdd');
    },
    [closePanel, openPanel, setQuickAddStartTime, setQuickAddDate, selectedDate]
  );

  // Handle FAB click - open quick add at current time
  const handleFabClick = useCallback(() => {
    closePanel('blockDetail');
    setQuickAddStartTime(undefined);
    setQuickAddDate(formatDateKey(selectedDate));
    openPanel('quickAdd');
  }, [closePanel, openPanel, setQuickAddStartTime, setQuickAddDate, selectedDate]);

  // Handle quick add save
  const handleQuickAddSave = useCallback(
    async (data: NewBlockData) => {
      try {
        await createBlock({
          date: data.date || formatDateKey(selectedDate),
          startTime: data.startTime,
          duration: data.duration,
          title: data.title,
          notes: data.notes,
          categoryId: data.categoryId,
          color: data.color,
          status: 'planned',
        });
      } catch (error) {
        console.error('Failed to create block:', error);
      }
    },
    [selectedDate, createBlock]
  );

  // Handle shutdown click - show confirmation first
  const handleShutdownClick = useCallback(() => {
    setIsShutdownConfirmOpen(true);
  }, []);

  // Handle confirmed shutdown - proceed to ritual
  const handleShutdownConfirm = useCallback(() => {
    setIsShutdownConfirmOpen(false);
    navigate('/shutdown');
  }, [navigate]);

  // Handle shutdown cancel
  const handleShutdownCancel = useCallback(() => {
    setIsShutdownConfirmOpen(false);
  }, []);

  // Handle settings click
  const handleSettingsClick = useCallback(() => {
    openPanel('settings');
  }, [openPanel]);

  // Handle task click
  const handleTaskClick = useCallback((task: Task) => {
    // TODO: Open task detail panel
    void task;
  }, []);

  // Handle task completion - hook handles errors internally
  const handleTaskComplete = useCallback((task: Task) => completeTask(task.id), [completeTask]);

  // Handle add task button click
  const handleAddTaskClick = useCallback(() => {
    closePanel('blockDetail');
    closePanel('quickAdd');
    openPanel('quickAddTask');
  }, [closePanel, openPanel]);

  // Handle schedule task - create block from task
  const handleScheduleTask = useCallback(
    (task: Task) => {
      closePanel('blockDetail');
      closePanel('quickAddTask');
      setSchedulingTask(task);
      setQuickAddStartTime(undefined);
      setQuickAddDate(formatDateKey(selectedDate));
      openPanel('quickAdd');
    },
    [closePanel, openPanel, setSchedulingTask, setQuickAddStartTime, setQuickAddDate, selectedDate]
  );

  // Handle task save - hook handles errors internally
  const handleTaskSave = useCallback(
    (data: NewTaskData) =>
      createTask({
        title: data.title,
        estimatedDuration: data.estimatedDuration,
        priority: data.priority,
        categoryId: data.categoryId,
        notes: data.notes,
      }),
    [createTask]
  );

  // Handle quick capture button click
  const handleQuickCaptureClick = useCallback(() => {
    closePanel('blockDetail');
    closePanel('quickAdd');
    openPanel('quickCapture');
  }, [closePanel, openPanel]);

  // Handle quick capture save - hook handles errors internally
  const handleCaptureSave = useCallback(
    (data: CaptureData) =>
      createCapture({
        content: data.content,
        type: data.type,
      }),
    [createCapture]
  );

  // Handle sidebar quick capture
  const handleSidebarCapture = useCallback(
    (content: string) => createCapture({ content, type: 'thought' }),
    [createCapture]
  );

  // Handle inbox convert to task
  const handleConvertToTask = useCallback(
    async (capture: Capture) => {
      try {
        // Create a new task from the capture
        const newTask = await createTask({
          title: capture.content,
          estimatedDuration: 30,
          priority: 'medium',
          categoryId: '',
          notes: '',
        });

        if (newTask) {
          // Mark capture as processed with link to new task
          await processCapture(capture.id, {
            convertTo: 'task',
            convertedId: newTask.id,
          });
        }
      } catch (error) {
        console.error('Failed to convert capture to task:', error);
      }
    },
    [createTask, processCapture]
  );

  // Handle inbox convert to block
  const handleConvertToBlock = useCallback(
    async (capture: Capture) => {
      try {
        // Create a new block from the capture
        const newBlock = await createBlock({
          date: formatDateKey(selectedDate),
          startTime: 540, // 9:00 AM default
          duration: 60,
          title: capture.content,
          notes: '',
          categoryId: '',
          color: '#2b6cb0',
          status: 'planned',
        });

        if (newBlock) {
          // Mark capture as processed with link to new block
          await processCapture(capture.id, {
            convertTo: 'block',
            convertedId: newBlock.id,
          });
        }
      } catch (error) {
        console.error('Failed to convert capture to block:', error);
      }
    },
    [createBlock, selectedDate, processCapture]
  );

  // Simple handlers - hooks handle errors internally
  const handleArchiveCapture = useCallback(
    (captureId: string) => archiveCapture(captureId),
    [archiveCapture]
  );

  const handleDeleteCapture = useCallback(
    (captureId: string) => deleteCapture(captureId),
    [deleteCapture]
  );

  const handleAddMIT = useCallback(
    (mit: { title: string; taskId?: string; blockId?: string; order: number }) => addMIT(mit),
    [addMIT]
  );

  const handleCompleteMIT = useCallback((mitId: string) => completeMIT(mitId), [completeMIT]);

  const handleRemoveMIT = useCallback((mitId: string) => removeMIT(mitId), [removeMIT]);

  // Handle converting a standalone MIT to a task
  const handleConvertMITToTask = useCallback(
    async (mit: DailyMIT) => {
      // Skip if already linked to a task
      if (mit.taskId) {
        console.warn('MIT is already linked to a task');
        return;
      }

      try {
        // Create a new task with the MIT's title
        const newTask = await createTask({
          title: mit.title,
          priority: 'high', // MITs are important, so default to high priority
        });

        if (newTask) {
          // Link the MIT to the newly created task
          await updateMIT(mit.id, { taskId: newTask.id });
          // Open the tasks panel to show the newly created task
          openPanel('tasks');
        }
      } catch (error) {
        console.error('Failed to convert MIT to task:', error);
      }
    },
    [createTask, updateMIT, openPanel]
  );

  // Handle duplicating a block
  const handleDuplicateBlock = useCallback(
    async (block: BlockWithRevisions) => {
      try {
        await createBlock({
          date: formatDateKey(selectedDate),
          startTime: block.startTime,
          duration: block.duration,
          title: block.title,
          notes: block.notes || '',
          categoryId: block.categoryId,
          color: block.color,
          status: 'planned',
        });
      } catch (error) {
        console.error('Failed to duplicate block:', error);
      }
    },
    [selectedDate, createBlock]
  );

  // Keyboard shortcut listener for Cmd+K / Ctrl+K to open quick capture
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        handleQuickCaptureClick();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleQuickCaptureClick]);

  return (
    <div className={styles.container}>
      {isInitialLoading ? (
        <DailyViewSkeleton />
      ) : (
        <>
          {/* Main Content */}
          <main className={styles.main}>
            {/* Header with date, metrics, and consolidated menu */}
            <Header
              date={selectedDate}
              completedCount={completedCount}
              totalCount={totalCount}
              mitsCompleted={mitsCompleted}
              mitsTotal={mitsTotal}
              deepWorkMinutes={dailyMetrics.deepWorkMinutes}
              shallowWorkMinutes={dailyMetrics.shallowWorkMinutes}
              missedMinutes={dailyMetrics.missedMinutes}
              onShutdownClick={handleShutdownClick}
              onSettingsClick={handleSettingsClick}
              onTasksClick={() => togglePanel('tasks')}
              onAddTaskClick={handleAddTaskClick}
            />

            {/* Missed shutdown banner */}
            <AnimatePresence>
              {showMissedBanner && (
                <MissedShutdownBanner
                  yesterdayDate={yesterdayKey}
                  onNavigateToShutdown={() => navigate('/shutdown')}
                  onDismiss={() => setIsBannerDismissed(true)}
                />
              )}
            </AnimatePresence>

            {/* Week strip navigation with day themes */}
            <WeekStrip
              selectedDate={selectedDate}
              onDateSelect={handleDateSelect}
              dayThemes={weeklyPlan?.dayThemes}
              ritualCompletedDates={ritualCompletedDates}
            />

            {/* MIT Selector - Today's Most Important Tasks */}
            <MITSelector
              mits={mits}
              tasks={tasks}
              blocks={blocks}
              onAddMIT={handleAddMIT}
              onCompleteMIT={handleCompleteMIT}
              onRemoveMIT={handleRemoveMIT}
              onConvertToTask={handleConvertMITToTask}
            />

            {/* Timeline with blocks */}
            <div className={styles.timelineWrapper}>
              <Timeline
                blocks={blocks}
                startHour={effectiveStartHour}
                endHour={effectiveEndHour}
                onBlockClick={handleBlockClick}
                onEmptySlotClick={handleEmptySlotClick}
              />
            </div>
          </main>

          {/* Right Sidebar - Weekly Goals & Inbox */}
          <RightSidebar
            selectedDate={selectedDate}
            inboxCaptures={inboxCaptures}
            onCreateCapture={handleSidebarCapture}
            onArchiveCapture={handleArchiveCapture}
            onDeleteCapture={handleDeleteCapture}
            onConvertToTask={handleConvertToTask}
            onConvertToBlock={handleConvertToBlock}
          />

          {/* FAB button - Blue for blocks */}
          <FAB onClick={handleFabClick} />

          {/* Quick Capture Button - Orange for capture */}
          <QuickCaptureButton onClick={handleQuickCaptureClick} />

          <QuickAddPanel onSave={handleQuickAddSave} />
          <BlockDetailPanel
            onSave={async (blockId, updates) => {
              const success = await updateBlock(blockId, updates);
              return success;
            }}
            onDelete={async (blockId) => {
              const success = await deleteBlock(blockId);
              return success;
            }}
            onMarkComplete={async (blockId) => {
              const success = await updateBlock(blockId, { status: 'completed' });
              return success;
            }}
            onMarkMissed={async (blockId, reason) => {
              const success = await updateBlock(blockId, {
                status: 'missed',
                ...(reason !== undefined && { missedReason: reason }),
                missedAt: Timestamp.now(),
              });
              return success;
            }}
            onDuplicate={handleDuplicateBlock}
          />
          <TaskPanelPanel
            tasks={tasks}
            onTaskClick={handleTaskClick}
            onTaskComplete={handleTaskComplete}
            onScheduleTask={handleScheduleTask}
            onSettingsClick={handleSettingsClick}
            onAddTask={handleAddTaskClick}
            onReorder={reorderTasks}
          />
          <QuickAddTaskPanel onSave={handleTaskSave} />
          <SettingsPanel />

          {/* Quick Capture Panel */}
          <QuickCapturePanel onSave={handleCaptureSave} />

          {/* Inbox Panel */}
          <InboxPanel
            captures={inboxCaptures}
            onConvertToTask={handleConvertToTask}
            onConvertToBlock={handleConvertToBlock}
            onArchive={handleArchiveCapture}
            onDelete={handleDeleteCapture}
          />

          {/* Fixed Schedule Reminder */}
          <FixedScheduleReminder />

          {/* Daily Stoic Quote - Floating Widget */}
          <DailyStoicQuote date={selectedDate} />

          {/* Subconscious Mind Affirmation - Floating Widget */}
          <SubconsciousMindQuote date={selectedDate} />

          {/* Shutdown Confirmation Dialog */}
          <ConfirmDialog
            isOpen={isShutdownConfirmOpen}
            title="Begin Shutdown Ritual"
            message={
              <>
                <p>
                  You're about to start your <strong>Shutdown Ritual</strong> - a mindful practice
                  to close your workday intentionally.
                </p>
                <p>This guided process includes:</p>
                <ul>
                  <li>Reviewing today's completed work</li>
                  <li>Handling unfinished tasks</li>
                  <li>Capturing any loose thoughts</li>
                  <li>Planning tomorrow's priorities</li>
                  <li>A moment of reflection</li>
                </ul>
                <p>
                  <strong>Note:</strong> Once started, complete the ritual to fully disconnect and
                  recharge for tomorrow.
                </p>
              </>
            }
            confirmLabel="Begin Ritual"
            cancelLabel="Not Yet"
            onConfirm={handleShutdownConfirm}
            onCancel={handleShutdownCancel}
            variant="info"
          />
        </>
      )}
    </div>
  );
}
